/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
 * Temp access email modal for manage roster flow
 *
 * @file TempAccessEmailModal.jsx
 * @author Anuja Gaikwad
 * @since 2.3.4
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  IconButton,
  Typography
} from '@mui/material';
import BannerCloseIcon from '../icons/BannerCloseIcon';
import Icons from '../icons/Icon';
import CopyableInlineLinkText from './copyableInlineLinkText/CopyableInlineLinkText';
import {
  getCopyTextFromSubInstructions,
  getLinkCopyText
} from './copyableInlineLinkText/copyableInlineLinkTextUtils';
import { TempAccessEmailModalStylesRoot, classes } from './tempAccessEmailModalStyles';

const TempAccessEmailModal = ({
  open,
  onClose,
  config,
  handleCopyRecipients,
  handleCopyInstructions
}) => {
  const [recipientsCopied, setRecipientsCopied] = React.useState(false);
  const [instructionsCopied, setInstructionsCopied] = React.useState(false);
  const [recipients, setRecipients] = React.useState('');

  const header = config?.header || {};
  const linkDetails = config?.linkDetails || [];
  const recipientsDetails = linkDetails[0] || {};
  const instructionsDetails = linkDetails[1] || {};
  const subInstructions = instructionsDetails?.subInstructions || [];
  const hasSubInstructions = Array.isArray(subInstructions) && subInstructions.length > 0;
  const {
    mainText,
    footerPrefix,
    footerUrl,
    footerLinkText
  } = instructionsDetails;
  const linkLabel = footerLinkText || footerUrl;
  const fallbackCopyText = footerUrl
    ? `${mainText || ''}\n\n${footerPrefix || ''}${getLinkCopyText(linkLabel, footerUrl)}`
    : (mainText || '');
  const instructionsCopyText = hasSubInstructions
    ? getCopyTextFromSubInstructions(subInstructions)
    : (instructionsDetails?.copyText || fallbackCopyText);

  React.useEffect(() => {
    if (open) {
      setRecipients(recipientsDetails?.linkText || config?.toRecipients || '');
      setRecipientsCopied(false);
      setInstructionsCopied(false);
    }
  }, [open, recipientsDetails?.linkText, config?.toRecipients]);

  const handleRecipientsCopy = () => {
    if (recipientsCopied || !recipients.trim()) {
      return;
    }

    navigator?.clipboard?.writeText(recipients);
    handleCopyRecipients(recipients);
    setRecipientsCopied(true);
    setTimeout(() => setRecipientsCopied(false), 2000);
  };

  const handleInstructionsCopy = () => {
    if (instructionsCopied || !instructionsCopyText) {
      return;
    }

    navigator?.clipboard?.writeText(instructionsCopyText);
    handleCopyInstructions(instructionsCopyText);
    setInstructionsCopied(true);
    setTimeout(() => setInstructionsCopied(false), 2000);
  };

  const renderRecipientsSection = () => {
    if (!recipientsDetails?.title) {
      return null;
    }

    return (
      <div className={classes.recipientsRow}>
        <div className={classes.recipientsFieldWrapper}>
          <Typography component="label" htmlFor="temp-access-email-recipients" className={classes.fieldLabel}>
            {recipientsDetails.title}
          </Typography>
          <input
            id="temp-access-email-recipients"
            type="text"
            value={recipients}
            onChange={(event) => setRecipients(event.target.value)}
            className={classes.recipientsField}
            aria-label={recipientsDetails.title}
          />
        </div>
        <Button
          classes={{
            root: classes.copyButton,
            startIcon: classes.copyButtonStartIcon
          }}
          startIcon={
            recipientsCopied ? (
              <Icons name="CopiedIcon" />
            ) : null
          }
          onClick={handleRecipientsCopy}
          disabled={!recipients.trim()}
        >
          {recipientsCopied ? recipientsDetails.copiedCTA : recipientsDetails.CTA}
        </Button>
      </div>
    );
  };

  const renderInstructionItem = (item, index) => {
    if (item.segments?.length) {
      return (
        <CopyableInlineLinkText
          key={item.id || `instruction-segment-${index}`}
          segments={item.segments}
          linkClassName={classes.inlineLink}
        />
      );
    }

    return (
      <span key={item.id || `instruction-text-${index}`}>
        {item.details}
      </span>
    );
  };

  const renderInstructionsSection = () => {
    if (!instructionsDetails?.title) {
      return null;
    }

    return (
      <div className={classes.instructionsGroup}>
        <Typography component="label" className={classes.fieldLabel}>
          {instructionsDetails.title}
        </Typography>
        <div
          className={classes.instructionsBodyInput}
          data-name="_input_text_area_field"
          role="textbox"
          aria-readonly="true"
          aria-label={instructionsDetails.title}
          tabIndex={0}
        >
          <Typography
            component="div"
            className={classes.instructionsBodyText}
          >
            {hasSubInstructions ? (
              subInstructions.map(renderInstructionItem)
            ) : (
              <>
                {mainText}
                {footerUrl ? (
                  <>
                    {'\n\n'}
                    {footerPrefix}
                    <a
                      href={footerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.inlineLink}
                      data-copy-text={getLinkCopyText(linkLabel, footerUrl)}
                    >
                      {linkLabel}
                    </a>
                  </>
                ) : null}
              </>
            )}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slots={{ paper: TempAccessEmailModalStylesRoot }}
      slotProps={{ paper: { className: classes.paper } }}
      maxWidth={false}
      scroll="paper"
    >
      <IconButton
        className={classes.closeButton}
        onClick={onClose}
        aria-label="Close email modal"
        disableRipple
      >
        <BannerCloseIcon className={classes.closeIcon} />
      </IconButton>
      <div className={classes.content}>
        <div className={classes.header}>
          <Typography component="h2" className={classes.title}>
            {header.title || config?.title || ''}
          </Typography>
          {(header.subTitle || config?.subTitle) && (
            <Typography component="p" className={classes.subTitle}>
              {header.subTitle || config?.subTitle}
            </Typography>
          )}
        </div>
        <div className={classes.body}>
          {renderRecipientsSection()}
          {renderInstructionsSection()}
        </div>
        {instructionsDetails?.CTA && (
          <div className={classes.footer}>
            <Button
              classes={{
                root: classes.copyButton,
                startIcon: classes.copyButtonStartIcon
              }}
              startIcon={
                instructionsCopied ? (
                  <Icons name="CopiedIcon" />
                ) : null
              }
              onClick={handleInstructionsCopy}
              disabled={!instructionsCopyText}
            >
              {instructionsCopied ? instructionsDetails.copiedCTA : instructionsDetails.CTA}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
};

TempAccessEmailModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  config: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    toRecipients: PropTypes.string,
    body: PropTypes.string,
    footerPrefix: PropTypes.string,
    footerUrl: PropTypes.string,
    students: PropTypes.array,
    header: PropTypes.shape({
      title: PropTypes.string,
      subTitle: PropTypes.string
    }),
    linkDetails: PropTypes.arrayOf(PropTypes.object)
  }),
  handleCopyRecipients: PropTypes.func,
  handleCopyInstructions: PropTypes.func
};

TempAccessEmailModal.defaultProps = {
  open: false,
  onClose: () => {},
  config: {},
  handleCopyRecipients: () => {},
  handleCopyInstructions: () => {}
};

export default TempAccessEmailModal;
